import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Modal from '../../../components/modal/modal.component';
import {IonAlert, IonCol, IonGrid, IonItem, IonList, IonRow, IonSelectOption, useIonViewWillLeave} from '@ionic/react';
import {Toast} from "@capacitor/toast";
import moment, {Moment} from "moment";
import {ReportRowComponentLabel} from "./labelScannerModal.component";
import {StyledModalContent} from "../../../components/modal/modal.style";
import {StyledContent} from "./commentsModal.style";
import {StyledButton, StyledCircleButton} from "../../../components/button/button.style";
import {StyledIonSelect} from "../../../components/form/input/select.style";
import CloseImage from "../../../assets/images/e-remove.svg";
import TrashRedImage from "../../../assets/images/trash-can-red.svg";
import {Order} from "../reportPage.component";
import {ReportSetting} from "@enums/report";
import {isSettingEnabled, getCustomName} from "@services/report.service";
import {
    checkCameraPermission,
    openSettings,
    startContinuousScan,
    startBatchContinuousScan,
    makeWebViewHidden,
    restoreWebView,
    toggleTorch,
    BARCODE_QR_AND_DATAMATRIX,
    BARCODE_1D_FORMATS_WITH_UPC,
    ContinuousScanHandle,
} from "@services/barcodeScanner.service";
import type { Barcode } from "@services/barcodeScanner.service";

type ManualScannerModalProps = {
    isOpen: boolean;
    isQr: boolean;
    order?: Order;
    onClose: () => void;
    onSubmit: (label: ReportRowComponentLabel) => void;
};

type ManualScannedDetailValue = {
    data: string
    field: string|null
}

const ManualLabelScannerModal: React.FC<ManualScannerModalProps> = (props: ManualScannerModalProps) => {

    const {t} = useTranslation();
    const [showModal, updateShowModal] = useState<boolean>(false);
    const [showPermissionAlert, setShowPermissionAlert] = useState<boolean>(false);
    const [scanStartAt, setScanStartAt] = useState<Moment|undefined>();
    const [scanIntervalAt, setScanIntervalAt] = useState<Moment|undefined>();
    const [label, updateLabel] = useState<ReportRowComponentLabel>({
        comment: null,
        quantity: null,
        deliveryNote: null,
        levNumber: null,
        serialNumber: null,
        componentNumberText: null,
        batchNumber: null,
        materialNumber: null,
        measureResult1: null,
        measureResult2: null,
        measureResult3: null,
        customField1: null,
        customField2: null,
        customField3: null,
        customField4: null
    });
    const [scannedDetailsValues, updateScannedDetailsValues] = useState<ManualScannedDetailValue[]>();

    const scanHandleRef = useRef<ContinuousScanHandle | null>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const scannedRawValuesRef = useRef<Set<string>>(new Set());
    const finishScanRef = useRef<(() => void) | null>(null);
    const smoothedPositionsRef = useRef<Map<string, [number, number][]>>(new Map());

    const LERP = 0.45;

    const sortCornerPoints = (pts: [number, number][]): [number, number][] => {
        const cx = pts.reduce((s, [x]) => s + x, 0) / pts.length;
        const cy = pts.reduce((s, [, y]) => s + y, 0) / pts.length;
        return [...pts].sort(([ax, ay], [bx, by]) => Math.atan2(ay - cy, ax - cx) - Math.atan2(by - cy, bx - cx));
    };

    const bestRotation = (prev: [number, number][], sorted: [number, number][]): [number, number][] => {
        let bestScore = Infinity;
        let best = sorted;
        for (let r = 0; r < sorted.length; r++) {
            const rotated = [...sorted.slice(r), ...sorted.slice(0, r)] as [number, number][];
            const score = rotated.reduce((s, [nx, ny], i) => {
                const dx = nx - prev[i][0];
                const dy = ny - prev[i][1];
                return s + dx * dx + dy * dy;
            }, 0);
            if (score < bestScore) { bestScore = score; best = rotated; }
        }
        return best;
    };

    const drawBarcodeOverlay = (barcodes: Barcode[], scannedRawValues: Set<string>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        for (const barcode of barcodes) {
            if (!barcode.cornerPoints || barcode.cornerPoints.length < 4) continue;
            const rawValue = barcode.rawValue ?? '';
            const isScanned = scannedRawValues.has(rawValue);

            const sorted = sortCornerPoints(barcode.cornerPoints.map(([x, y]) => [x / dpr, y / dpr] as [number, number]));
            const prev = smoothedPositionsRef.current.get(rawValue);
            const aligned = prev ? bestRotation(prev, sorted) : sorted;
            const points: [number, number][] = prev
                ? aligned.map(([nx, ny], i) => [prev[i][0] + (nx - prev[i][0]) * LERP, prev[i][1] + (ny - prev[i][1]) * LERP])
                : aligned;

            smoothedPositionsRef.current.set(rawValue, points);

            ctx.beginPath();
            ctx.moveTo(points[0][0], points[0][1]);
            for (let i = 1; i < points.length; i++) {
                ctx.lineTo(points[i][0], points[i][1]);
            }
            ctx.closePath();

            ctx.strokeStyle = isScanned ? '#00ff44' : '#ffffff';
            ctx.lineWidth = 3;
            ctx.stroke();
        }

        const visible = new Set(barcodes.map(b => b.rawValue));
        smoothedPositionsRef.current.forEach((_, key) => {
            if (!visible.has(key)) smoothedPositionsRef.current.delete(key);
        });
    };

    const handleClose = () => {
        props.onClose();
        stopScan();
    };

    useEffect(() => {
        handleOpen();
    }, [props.isOpen]);

    let scanTimeout: NodeJS.Timeout | undefined;

    useEffect(() => {
        checkScanInterval();
        if (scanTimeout) clearInterval(scanTimeout);

        if (scanStartAt) {
            scanTimeout = setInterval(checkScanInterval, 1000);
        }

        return () => {
            if (scanTimeout) clearInterval(scanTimeout);
        };
    }, [scanIntervalAt])

    const checkScanInterval = () => {
        let now = moment();
        if (scanStartAt && now.diff(scanStartAt, 's') > (props.order?.ep2_scanner_timeout ? parseInt(props.order.ep2_scanner_timeout) : 15)) {
            if (finishScanRef.current) {
                finishScanRef.current();
            } else {
                stopScan();
            }
        }
    }

    const handleOpen = () => {
        updateShowModal(props.isOpen);
        updateLabel({
            comment: null,
            quantity: null,
            deliveryNote: null,
            levNumber: null,
            serialNumber: null,
            componentNumberText: null,
            batchNumber: null,
            materialNumber: null,
            measureResult1: null,
            measureResult2: null,
            measureResult3: null,
            customField1: null,
            customField2: null,
            customField3: null,
            customField4: null
        });

        if (props.isOpen) {
            updateScannedDetailsValues([]);
            setScanStartAt(moment());
            setScanIntervalAt(moment());
            scanLabel();
        }
    };

    const handleSubmit = async () => {
        if (scannedDetailsValues) {
            let tmpLabel = matchDetailDataWithLabel(scannedDetailsValues);
            if (tmpLabel) {
                props.onSubmit(tmpLabel);
            }
        }
    };

    useIonViewWillLeave(() => {
        stopScan();
    });

    const startScan = async () => {
        let scanStarted:any = undefined;
        let scanned:ManualScannedDetailValue[] = [];
        makeWebViewHidden();

        const formats = props.isQr ? BARCODE_QR_AND_DATAMATRIX : BARCODE_1D_FORMATS_WITH_UPC;

        const handle = await startContinuousScan(formats, async (rawValue) => {
            let now = moment();
            if (scanStarted === undefined) {
                scanStarted = now;
                setScanStartAt(now);
            }

            if (rawValue) {
                if (props.isQr) {
                    let tmpScannedDetailsValues:ManualScannedDetailValue[] = [];
                    let qrData;
                    if (isValidGS1DataMatrix(rawValue)) {
                        qrData = parseGS1DataMatrixCode(rawValue);
                    } else if (isValidQr(rawValue)) {
                        qrData = prepareAiQrData(rawValue);
                    } else if (isValidSingleCharPrefixCode(rawValue)) {
                        qrData = parseSingleCharPrefixCode(rawValue);
                    } else if (isValidVWDataMatrix(rawValue)) {
                        qrData = parseVWQrData(rawValue);
                    } else {
                        qrData = parseGenericQrData(rawValue);
                    }

                    if (qrData) {
                        for (const key in qrData) {
                            if (qrData.hasOwnProperty(key)) {
                                const entry = qrData[key];
                                if (tmpScannedDetailsValues.filter(tmpDatum => {
                                    return entry.matchedField === tmpDatum.field;
                                }).length > 0) {
                                    tmpScannedDetailsValues.push({
                                        data: entry.value,
                                        field: null,
                                    })
                                } else {
                                    tmpScannedDetailsValues.push({
                                        data: entry.value,
                                        field: entry.matchedField,
                                    })
                                }
                            }
                        }
                    }

                    updateScannedDetailsValues(tmpScannedDetailsValues);
                    setScanIntervalAt(moment());
                    stopScan();
                    return;
                } else {
                    const matchedField = matchFieldWithSingleCharPrefix(rawValue.charAt(0));

                    const data = matchedField && !props.order?.scanning_settings.manualKeepPrefix
                        ? rawValue.substring(1)
                        : rawValue;

                    const isDuplicate = scanned.some(entry => matchedField
                        ? entry.field === matchedField
                        : entry.field === null && entry.data === data);

                    if (!isDuplicate) {
                        scanned.push({ data, field: matchedField });

                        await Toast.show({
                            text: data,
                            duration: 'short',
                            position: 'bottom',
                        });
                    }
                }
            }

            updateScannedDetailsValues([ ...scanned ]);
            setScanIntervalAt(moment());

            if (now.diff(scanStarted, 's') > (props.order?.ep2_scanner_timeout ? parseInt(props.order.ep2_scanner_timeout) : 15)) {
                stopScan();
            }
        }, { enableTorch: props.order?.scanning_settings.torchDefault ?? false });

        scanHandleRef.current = handle;
    };

    // ── Batch scan ─────────────────────────────────────────────────────
    // Collects all codes across frames. QR: stops after first valid QR.
    // 1D: accumulates unique codes, waits 3s idle or overall timeout,
    // then stops and shows results for manual field matching.
    const startBatchScan = async () => {
        let scanStarted: any = undefined;
        let scanned: string[] = [];
        let toastBuffer: string[] = [];
        let toastTimeout: NodeJS.Timeout | null = null;
        scannedRawValuesRef.current = new Set();
        smoothedPositionsRef.current = new Map();
        makeWebViewHidden();

        const formats = props.isQr ? BARCODE_QR_AND_DATAMATRIX : BARCODE_1D_FORMATS_WITH_UPC;

        const flushToast = () => {
            if (toastBuffer.length > 0) {
                Toast.show({ text: toastBuffer.join(', '), duration: 'short', position: 'bottom' });
                toastBuffer = [];
            }
            toastTimeout = null;
        };

        const finish1D = () => {
            if (toastTimeout) clearTimeout(toastTimeout);
            flushToast();

           const keepPrefix = props.order?.scanning_settings.manualKeepPrefix;
            let tmpScannedDetailsValues: ManualScannedDetailValue[] = [];

            for (const rawValue of scanned) {
                const matchedField = matchFieldWithSingleCharPrefix(rawValue.charAt(0));
                const data = matchedField && !keepPrefix
                    ? rawValue.substring(1)
                    : rawValue;

                const isFieldTaken = matchedField !== null && tmpScannedDetailsValues.some(entry => entry.field === matchedField);

                tmpScannedDetailsValues.push({
                    data,
                    field: isFieldTaken ? null : matchedField,
                });
            }

            updateScannedDetailsValues(tmpScannedDetailsValues);
            stopScan();
        };

        if (!props.isQr) {
            finishScanRef.current = finish1D;
        }

        const handle = await startBatchContinuousScan(formats, async (scannedBarcodes) => {
            const rawValues = scannedBarcodes.map(b => b.rawValue).filter((v): v is string => !!v);
            let now = moment();
            if (scanStarted === undefined) {
                scanStarted = now;
                setScanStartAt(now);
            }

            drawBarcodeOverlay(scannedBarcodes, scannedRawValuesRef.current);

            if (props.isQr) {
                const rawValue = rawValues[0];
                if (!rawValue) return;

                let tmpScannedDetailsValues: ManualScannedDetailValue[] = [];
                let qrData;
                if (isValidGS1DataMatrix(rawValue)) {
                    qrData = parseGS1DataMatrixCode(rawValue);
                } else if (isValidQr(rawValue)) {
                    qrData = prepareAiQrData(rawValue);
                } else if (isValidVWDataMatrix(rawValue)) {
                    qrData = parseVWQrData(rawValue);
                } else {
                    qrData = parseGenericQrData(rawValue);
                }

                if (qrData) {
                    for (const key in qrData) {
                        if (qrData.hasOwnProperty(key)) {
                            const entry = qrData[key];
                            if (tmpScannedDetailsValues.filter(tmpDatum => {
                                return entry.matchedField === tmpDatum.field;
                            }).length > 0) {
                                tmpScannedDetailsValues.push({
                                    data: entry.value,
                                    field: null,
                                });
                            } else {
                                tmpScannedDetailsValues.push({
                                    data: entry.value,
                                    field: entry.matchedField,
                                });
                            }
                        }
                    }
                }

                updateScannedDetailsValues(tmpScannedDetailsValues);
                setScanIntervalAt(moment());
                stopScan();
            } else {
                let newInFrame: string[] = [];

                for (const rawValue of rawValues) {
                    if (!scanned.includes(rawValue)) {
                        scanned.push(rawValue);
                        scannedRawValuesRef.current.add(rawValue);
                        newInFrame.push(rawValue);
                    }
                }

                if (newInFrame.length > 0) {
                    toastBuffer.push(...newInFrame);
                    if (toastTimeout) clearTimeout(toastTimeout);
                    toastTimeout = setTimeout(flushToast, 500);
                }

                if (newInFrame.length > 0) {
                    setScanIntervalAt(moment());
                }

                // Overall timeout
                if (now.diff(scanStarted, 's') > (props.order?.ep2_scanner_timeout ? parseInt(props.order.ep2_scanner_timeout) : 15)) {
                    finish1D();
                }
            }
        }, { enableTorch: props.order?.scanning_settings.torchDefault ?? false });

        scanHandleRef.current = handle;
    };

    const stopScan = async () => {
        finishScanRef.current = null;
        await scanHandleRef.current?.stop();
        scanHandleRef.current = null;
        restoreWebView();
        const canvas = canvasRef.current;
        if (canvas) canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);

        setScanStartAt(undefined)
        setScanIntervalAt(undefined)

        if (scanTimeout) clearInterval(scanTimeout);
    };

    const scanLabel = async () => {
        let hasPermission = await checkCameraPermission();
        if (!hasPermission) {
            setShowPermissionAlert(true);
        }
        startBatchScan();
    }

    const labelNames: string[] = [
        'componentNumberText',
        'quantity',
    ]

    if (props.order) {
        if (isSettingEnabled(ReportSetting.DELIVERY_NOTE, props.order)) {
            labelNames.push('deliveryNote');
        }

        if (isSettingEnabled(ReportSetting.LEV_NUMBER, props.order)) {
            labelNames.push('levNumber');
        }

        if (isSettingEnabled(ReportSetting.SERIAL_NUMBER, props.order)) {
            labelNames.push('serialNumber');
        }

        if (isSettingEnabled(ReportSetting.BATCH_NUMBER, props.order)) {
            labelNames.push('batchNumber');
        }

        if (isSettingEnabled(ReportSetting.MATERIAL_NUMBER, props.order)) {
            labelNames.push('materialNumber');
        }

        if (isSettingEnabled(ReportSetting.MEASURE_RESULT_1, props.order)) {
            labelNames.push('measureResult1');
        }

        if (isSettingEnabled(ReportSetting.MEASURE_RESULT_2, props.order)) {
            labelNames.push('measureResult2');
        }

        if (isSettingEnabled(ReportSetting.MEASURE_RESULT_3, props.order)) {
            labelNames.push('measureResult3');
        }

        if (isSettingEnabled(ReportSetting.CUSTOM_FIELD_1, props.order)) {
            labelNames.push("customField1");
        }

        if (isSettingEnabled(ReportSetting.CUSTOM_FIELD_2, props.order)) {
            labelNames.push("customField2");
        }

        if (isSettingEnabled(ReportSetting.CUSTOM_FIELD_3, props.order)) {
            labelNames.push("customField3");
        }

        if (isSettingEnabled(ReportSetting.CUSTOM_FIELD_4, props.order)) {
            labelNames.push("customField4");
        }
    }

    const possibleLabelNames = (selected: ManualScannedDetailValue) => {
        return labelNames.filter(value => {
            let stateVal = scannedDetailsValues?.filter(v => {
                return v.field == value
            })[0];

            return stateVal === undefined || stateVal == selected;
        });
    }

    const fieldToCustomNameSetting: Record<string, ReportSetting> = {
        'componentNumberText': ReportSetting.COMPONENT_NUMBER_CUSTOM_NAME,
        'deliveryNote': ReportSetting.DELIVERY_NOTE_CUSTOM_NAME,
        'levNumber': ReportSetting.LEV_NUMBER_CUSTOM_NAME,
        'serialNumber': ReportSetting.SERIAL_NUMBER_CUSTOM_NAME,
        'batchNumber': ReportSetting.BATCH_NUMBER_CUSTOM_NAME,
        'materialNumber': ReportSetting.MATERIAL_NUMBER_CUSTOM_NAME,
        'measureResult1': ReportSetting.MEASURE_RESULT_1_CUSTOM_NAME,
        'measureResult2': ReportSetting.MEASURE_RESULT_2_CUSTOM_NAME,
        'measureResult3': ReportSetting.MEASURE_RESULT_3_CUSTOM_NAME,
        'customField1': ReportSetting.CUSTOM_FIELD_1_CUSTOM_NAME,
        'customField2': ReportSetting.CUSTOM_FIELD_2_CUSTOM_NAME,
        'customField3': ReportSetting.CUSTOM_FIELD_3_CUSTOM_NAME,
        'customField4': ReportSetting.CUSTOM_FIELD_4_CUSTOM_NAME,
    };

    const getFieldDisplayName = (fieldName: string): string => {
        if (props.order && fieldToCustomNameSetting[fieldName]) {
            const customName = getCustomName(fieldToCustomNameSetting[fieldName], props.order);
            if (customName) {
                return customName;
            }
        }
        return t('reportPage.reportForm.nestedReporting.' + fieldName);
    };

    const matchWithField = (scannedDetailsValue: ManualScannedDetailValue, index: number, value: string) => {
        const tmp = [ ...(scannedDetailsValues || []) ];
        const valueIndex = scannedDetailsValues?.indexOf(scannedDetailsValue)
        if (valueIndex !== undefined && valueIndex !== -1) {
            tmp[valueIndex].field = value;
        }
        updateScannedDetailsValues(tmp)
    }

    const matchDetailDataWithLabel = (data: ManualScannedDetailValue[]) => {
        let tmpLabel = label;
        for (const item of data) {
            switch (item.field) {
                case 'deliveryNote':
                    tmpLabel.deliveryNote = item.data;
                    break;
                case 'componentNumberText':
                    tmpLabel.componentNumberText = item.data;
                    break;
                case 'levNumber':
                    tmpLabel.levNumber = item.data;
                    break;
                case 'serialNumber':
                    tmpLabel.serialNumber = item.data;
                    break;
                case 'quantity':
                    tmpLabel.quantity = item.data.replace(/[^0-9]/g, '');
                    break;
                case 'batchNumber':
                    tmpLabel.batchNumber = item.data;
                    break;
                case 'materialNumber':
                    tmpLabel.materialNumber = item.data;
                    break;
                case 'measureResult1':
                    tmpLabel.measureResult1 = item.data;
                    break;
                case 'measureResult2':
                    tmpLabel.measureResult2 = item.data;
                    break;
                case 'measureResult3':
                    tmpLabel.measureResult3 = item.data;
                    break;
                case 'customField1':
                    tmpLabel.customField1 = item.data;
                    break;
                case 'customField2':
                    tmpLabel.customField2 = item.data;
                    break;
                case 'customField3':
                    tmpLabel.customField3 = item.data;
                    break;
                case 'customField4':
                    tmpLabel.customField4 = item.data;
                    break;
            }
        }

        return tmpLabel;
    }

    const matchFieldWithAiCode = (code: string) => {
        switch (code) {
            case '00':
            case '21':
                return 'serialNumber';
            case '10':
                return 'batchNumber';
            case '30':
                return 'quantity';
            case '91':
            case '92':
                return 'deliveryNote';
            case '241':
                return 'componentNumberText';
            case '251':
                return 'levNumber';
            default:
                return null;
        }
    }

    const matchFieldWithAiGS1DataMatrixCode = (code: string) => {
        const ai = code.substring(0, 2); // Get the first two characters as AI

        if (ai === '1P') {
            return 'componentNumberText';
        } else if (code.startsWith('Q')) {
            return 'quantity';
        } else if (ai === '1T') {
            return 'batchNumber';
        } else if (code.startsWith('16D')) {
            return 'deliveryNote';
        }

        return null;
    }

    const matchFieldWithSingleCharPrefix = (prefix: string) => {
        switch (prefix) {
            case 'N':
                return 'deliveryNote';
            case 'P':
                return 'componentNumberText';
            case 'V':
                return 'levNumber';
            case 'S':
            case 'M':
            case 'X':
                return 'serialNumber';
            case 'Q':
                return 'quantity';
            case 'H':
                return 'batchNumber';
            default:
                return null;
        }
    }

    const splitGenericQrParts = (data: string) => {
        const replacedString = data.replace(/[^\w\s]/g, ';'); // [^\w\s] dopasowuje wszystkie znaki oprócz liter, cyfr i białych znaków

        return replacedString.split(';').filter(Boolean);
    }

    const prepareAiQrData = (data: string) => {
        const pattern = /\((\d{2,3})\)([^()]+)/g;
        const matchesArray = Array.from(data.matchAll(pattern));
        const parsedData: { [key: string]: { description: string, value: string, matchedField: string|null } } = {};

        const aiDescriptions: { [key: string]: string } = {
            '00': 'Serial Shipping Container Code (SSCC)',
            '01': 'Global Trade Item Number (GTIN)',
            '10': 'Batch or Lot Number',
            '11': 'Production Date (YYMMDD)',
            '13': 'Packaging Date (YYMMDD)',
            '15': 'Best Before Date (YYMMDD)',
            '17': 'Expiration Date (YYMMDD)',
            '20': 'Internal Product Variant',
            '21': 'Serial Number',
            '22': 'Secondary Data Fields',
            '30': 'Count of Items',
            '37': 'Count of Trade Items',
            '240': 'Additional Product Identification',
            '241': 'Customer Part Number',
            '242': 'Made-To-Order Variation Number',
            '250': 'Secondary Serial Number',
            '251': 'Reference to Source Entity',
            '253': 'Global Document Type Identifier (GDTI)',
            '254': 'GLN Extension Component',
            '400': 'Customer Purchase Order Number',
            '401': 'GINC (Global Identification Number for Consignment)',
            '402': 'GSIN (Global Shipment Identification Number)',
            '403': 'Routing Code',
            '410': 'Ship To - Deliver To GLN',
            '411': 'Bill To - Invoice To GLN',
            '412': 'Purchased From GLN',
            '413': 'Ship For - Deliver For - GLN',
            '414': 'Identification of a Physical Location (GLN)',
            '415': 'GLN of the Invoicing Party',
            '416': 'GLN of the Production or Service Location',
            '420': 'Ship To Postal Code within a Single Postal Authority',
            '421': 'Ship To Postal Code with Three-Digit ISO Country Code',
            '422': 'Country of Origin of a Trade Item',
            '423': 'Country of Initial Processing',
            '424': 'Country of Processing',
            '425': 'Country of Disassembly',
            '426': 'Country of Full Disassembly',
            '427': 'Country of Partial Disassembly',
        };

        const keepPrefix = props.order?.scanning_settings.manualKeepPrefix;

        for (const match of matchesArray) {
            if (match[1] && match[2]) {
                const ai = match[1];
                const value = keepPrefix ? match[1] + match[2] : match[2];
                const description = aiDescriptions[ai] || 'Unknown AI';
                const matchedField = matchFieldWithAiCode(ai);
                parsedData[ai] = { description, value, matchedField };
            }
        }

        return parsedData;
    }

    function isValidGS1DataMatrix(input: string): boolean {
        return input.startsWith('[)>') && input.endsWith('\x04')
    }

    function isValidVWDataMatrix(input: string): boolean {
        return input.includes('MHD');
    }

    function isValidQr(input: string): boolean {
        const pattern = /\((\d{2,3})\)([^()]+)/g;
        const matchesArray = Array.from(input.matchAll(pattern));
        return matchesArray.length > 0;
    }

    function isValidSingleCharPrefixCode(input: string): boolean {
        const parts = splitGenericQrParts(input);

        // Leave single-segment codes to the fallback - a prefix is indistinguishable from a plain value (e.g. "VOLVO" -> "OLVO")
        if (parts.length < 2) {
            return false;
        }

        // Require EVERY segment to carry a known prefix - otherwise the code belongs to parseGenericQrData
        return parts.every(part => part.length >= 2 && matchFieldWithSingleCharPrefix(part.charAt(0)) !== null);
    }

    function parseGS1DataMatrixCode(input: string){
        const parsedData: { [key: string]: { description: string, value: string, matchedField: string|null } } = {};

        if (!isValidGS1DataMatrix(input)) {
            return null;
        }

        // Remove leading '[)>' and trailing EOT '\x04'
        const trimmedInput = input.slice(3, -1);

        // Split the string by the delimiter '\x1E'
        const parts = trimmedInput.split('\x1E');

        // Remove empty elements that might result from splitting
        const cleanedParts = parts.filter(part => part.trim() !== '');

        // Further split each part by the delimiter '\x1D'
        const parsedParts = cleanedParts.flatMap(part => part.split('\x1D'));

        const keepPrefix = props.order?.scanning_settings.manualKeepPrefix;

        parsedParts.forEach(part => {
            const matchedField = matchFieldWithAiGS1DataMatrixCode(part);
            const ai = part.substring(0, 2); // Get the first two characters as AI
            let value;

            if (ai === '1P') {
                value = keepPrefix ? part : part.substring(2)
            } else if (part.startsWith('Q')) {
                value = keepPrefix ? part : part.substring(1);
            } else if (ai === '1T') {
                value = keepPrefix ? part : part.substring(2)
            } else if (part.startsWith('16D')) {
                value = keepPrefix ? part : part.substring(3)
            }
            if (value) {
                const description = ai;
                parsedData[ai] = { description, value, matchedField };
            }
        });

        return parsedData;
    }

    const parseSingleCharPrefixCode = (data: string) => {
        const parsedData: { [key: string]: { description: string, value: string, matchedField: string|null } } = {};
        const parts = splitGenericQrParts(data);

        const keepPrefix = props.order?.scanning_settings.manualKeepPrefix;

        parts.forEach(part => {
            const prefix = part.charAt(0);
            const matchedField = matchFieldWithSingleCharPrefix(prefix);
            const value = keepPrefix ? part : part.substring(1);

            parsedData[prefix] = { description: prefix, value, matchedField };
        });

        return parsedData;
    }

    const parseVWQrData = (data: string) => {
        return parseGenericQrData(data);
    }

    const parseGenericQrData = (data: string) => {
        const parsedData: { [key: string]: { description: string, value: string, matchedField: string|null } } = {};
        const matchesArray = splitGenericQrParts(data);

        matchesArray.forEach((value, key) => {
            let matchedField = null;
            let description = '';
            parsedData[key] = { description, value, matchedField };
        })

        return parsedData;
    }

    const removeData = (scannedDetailsValue: ManualScannedDetailValue) => {
        const tmp = [ ...(scannedDetailsValues || []) ];
        const valueIndex = scannedDetailsValues?.indexOf(scannedDetailsValue)
        if (valueIndex !== undefined && valueIndex !== -1) {
            tmp.splice(valueIndex, 1);
        }
        updateScannedDetailsValues(tmp)
    }

    return (
        <Modal
            isOpen={showModal}
            disableClose={true}
            onDidDismiss={() => handleClose()}>

            <StyledModalContent className="modal-wrap">
                <img src={CloseImage} className="close" onClick={() => handleClose()}/>
                <h1 className="title">{t('reportPage.manualLabel.title')}</h1>
                <StyledContent className="content">
                    {scannedDetailsValues && scannedDetailsValues.length > 0 && scannedDetailsValues.map((value, i) =>
                        <IonRow key={i}>
                            <IonCol size="5">
                                <p className="listLabel">{value.data}</p>
                            </IonCol>
                            <IonCol size="6">
                                <StyledIonSelect className="bolded"
                                                 okText={t('common.selectOk')}
                                                 cancelText={t('common.selectCancel')}
                                                 justify="end"
                                                 value={value.field}
                                                 interfaceOptions={
                                                     {
                                                         header: t('common.select')
                                                     }
                                                 }
                                                 onIonChange={e => {
                                                     matchWithField(value, i, e.detail.value)
                                                 }}>
                                    <IonSelectOption value="">
                                        {t('common.selectEmpty')}
                                    </IonSelectOption>
                                    {possibleLabelNames(value).map((possibleValue: string, key) =>
                                        <IonSelectOption key={key}
                                                         value={possibleValue}>{getFieldDisplayName(possibleValue)}</IonSelectOption>)}
                                </StyledIonSelect>
                            </IonCol>
                            <IonCol size="1">
                                <StyledCircleButton className="white" onClick={() => removeData(value)}><img src={TrashRedImage}/></StyledCircleButton>
                            </IonCol>
                        </IonRow>
                    )}

                    <StyledButton disabled={scannedDetailsValues?.length === 0}
                                  onClick={handleSubmit}>{t('reportPage.manualLabel.save')}</StyledButton>
                </StyledContent>
            </StyledModalContent>

            {showModal && (
                <>
                    <canvas
                        ref={canvasRef}
                        className="scanner-barcode-overlay"
                        width={window.innerWidth}
                        height={window.innerHeight}
                    />
                    <div className="scanner-close-button">
                        <button onClick={() => handleClose()}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/>
                                <line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>
                    <div className="scanner-torch-button">
                        <button onClick={() => toggleTorch()}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="white">
                                <path d="M6 2h12l-2 6h2l-6 10v-6H8l2-6H6z"/>
                            </svg>
                        </button>
                    </div>
                </>
            )}

            <IonAlert
                isOpen={showPermissionAlert}
                onDidDismiss={() => setShowPermissionAlert(false)}
                header={t('common.permissions.alert')}
                buttons={[
                    {
                        text: t('common.alertCancel'),
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            setShowPermissionAlert(false);
                        },
                    },
                    {
                        text: t('common.permissions.goToAppSettings'),
                        handler: async () => {
                            setShowPermissionAlert(false);
                            openSettings();
                        }
                    }
                ]}
            />
        </Modal>
    );
};

export default ManualLabelScannerModal;
